import SoftwareIntegratorIcon from '@assets/software_integrator.svg?react';
import { BlogCard } from '@blog/components/display/cards';
import { useGetBlogPostsQuery } from '@blog/services/blog';
import ArrowRightAlt from '@mui/icons-material/ArrowRightAlt';
import MessageIcon from '@mui/icons-material/Message';
import { Drawer, IconButton, List, ListItem, Typography } from '@mui/material';
import { useGetSoftwareIntegratorsQuery } from '@softwareIntegrator/services/softwareIntegrators';
import { ISoftwareIntegratorDto } from '@softwareIntegrator/types/softwareIntegrator';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { InfoAccordion } from '../../components/display/accordions/InfoAccordion';
import { ContactBox } from '../../components/display/boxes/ContactBox';
import { MapDialog } from '../../components/display/dialogs/MapDialog';
import { Map } from '../../components/display/maps/Map/Map';
import { ContactForm } from '../../components/utility/forms/ContactForm';

export const SOFTWARE_INTEGRATOR_PDF_URL = 'https://drive.google.com/file/d/1auxvIfTvAOxOX75ck-ZknXgTFqhHOdM2/view';
export const SOFTWARE_INTEGRATOR_BLOG_URL =
    'https://www.victronenergy.com/blog/category/technical-articles/software-integrator-program/';
export const RECOMMENDED_SOFTWARE_INTEGRATOR_BLOG_URL =
    'https://www.victronenergy.com/blog/2024/12/11/victron-recommended-software-integrator-program/';
export const RECOMMENDED_SOFTWARE_INTEGRATOR_BLOG_URL_2 =
    'https://www.victronenergy.com/blog/2024/12/27/victron-recommended-software-integrator-program-2/';
export const SOFTWARE_INTEGRATOR_FORM_URL =
    'https://docs.google.com/forms/d/e/1FAIpQLSe2LphxPvOvFJDK3FrUVLmGjRYrV517w8I4SFRfO_EXlyhvFA/viewform';
export const SOFTWARE_INTEGRATOR_EMAIL_ADDRESS = 'softwareintegrators@victronenergy.com';

enum ACCORDION_SECTIONS {
    OUR_MISSION = 'our_mission',
    TOOLS = 'tools',
    SUPPORTING_INSTALLERS = 'supporting_installers',
    NEXT_STEPS = 'next_steps',
    SOFTWARE_INTEGRATORS = 'software_integrators',
}

export const SoftwareIntegrator = () => {
    const [accordionStates, setAccordionStates] = useState<Record<ACCORDION_SECTIONS, boolean>>({
        our_mission: true,
        tools: false,
        supporting_installers: false,
        next_steps: false,
        software_integrators: false,
    });

    const [contactBoxOpen, setContactBoxOpen] = useState(false);
    const [contactFormOpen, setContactFormOpen] = useState(false);
    const [openedIntegrator, setOpenedIntegrator] = useState<ISoftwareIntegratorDto | null>(null);

    const handleAccordionClick = useCallback((accordion: ACCORDION_SECTIONS) => {
        setAccordionStates((prev) => ({ ...prev, [accordion]: !prev[accordion] }));
    }, []);
    const handleChooseIntegrator = useCallback(
        (integrator: ISoftwareIntegratorDto | null) => setOpenedIntegrator(integrator),
        []
    );

    const handleContactDrawerFactory = useCallback((open: boolean) => () => setContactBoxOpen(open), []);
    const handleContactFormOpenFactory = useCallback((open: boolean) => () => setContactFormOpen(open), []);

    const { data } = useGetSoftwareIntegratorsQuery({});
    const { data: relatedBlogPosts } = useGetBlogPostsQuery({ per_page: 3, categories: 264 });

    return (
        <>
            <div className="px-4 md:px-16 pt-11 pb-20" id="map">
                <div className="w-full h-[600px]">
                    <Map integrators={data?.results ?? []} onMarkerButtonClick={handleChooseIntegrator} />
                </div>
                <div className="flex gap-4 mt-4">
                    <div className="flex flex-col gap-4 lg:flex-[0.6] [&_p]:my-4 max-w-full">
                        <InfoAccordion
                            expanded={accordionStates.our_mission}
                            onChange={() => handleAccordionClick(ACCORDION_SECTIONS.OUR_MISSION)}
                            title={
                                <FormattedMessage
                                    id="softwareIntegrator.ourMission.title"
                                    defaultMessage="Our Mission"
                                />
                            }
                        >
                            <SoftwareIntegratorIcon className="w-full h-auto" />
                            <p>
                                <FormattedMessage
                                    id="softwareIntegrator.ourMission.description"
                                    defaultMessage="Many of our customers require bespoke solutions to unlock the full 
                                        potential of their systems and meet their specific needs. That's why we created 
                                        the <strong>Software Integrator Program</strong>."
                                    values={{ strong: (chunks) => <strong>{chunks}</strong> }}
                                />
                            </p>
                            <p>
                                <FormattedMessage
                                    id="softwareIntegrator.ourMission.description2"
                                    defaultMessage="This program equips installers with the tools and expertise to tailor their Victron
                                        systems, enabling businesses to quickly and effectively develop solutions that meet
                                        specific needs. By connecting you with qualified software integrators, we aim to
                                        accelerate the development of practical, <strong>customized</strong> solutions for
                                        your projects."
                                    values={{ strong: (chunks) => <strong>{chunks}</strong> }}
                                />
                            </p>
                            <p>
                                <FormattedMessage
                                    id="softwareIntegrator.ourMission.description3"
                                    defaultMessage="Through the Victron Recommended Software Integrator Program, we aim to connect
                                        businesses with skilled software integrators, helping them customise their Victron
                                        systems to meet specific needs."
                                />
                            </p>
                        </InfoAccordion>
                        <InfoAccordion
                            onChange={() => handleAccordionClick(ACCORDION_SECTIONS.TOOLS)}
                            expanded={accordionStates.tools}
                            title={
                                <FormattedMessage
                                    id="softwareIntegrator.tools.title"
                                    defaultMessage="Tools for bespoke solutions"
                                />
                            }
                        >
                            <p>
                                <FormattedMessage
                                    id="softwareIntegrator.tools.description"
                                    defaultMessage="The Victron Recommended Software Integrator Program bridges the gap between the
                                        Victron ecosystem and client-specific needs, enabling deep integrations,
                                        automations, and customisations for special projects."
                                />
                            </p>
                            <List sx={{ listStyleType: 'disc', '& > li': { marginLeft: 2 } }}>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.tools.nodeRed"
                                        defaultMessage="<b>Node-RED:</b> Open-source modules enhance connectivity with third-party software and hardware, while customizable dashboards facilitate intuitive control and monitoring."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.tools.mqtt"
                                        defaultMessage="<b>MQTT:</b> Enables bidirectional, real-time system monitoring and control through an industry-standard protocol."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.tools.vrmApi"
                                        defaultMessage="<b>VRM API:</b> Integrates Victron's Remote Monitoring Portal, enabling existing fleet controls and monitoring through a unified interface."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.tools.venusOs"
                                        defaultMessage="<b>Venus OS - custom interface development:</b> Modify low level interfaces for specific applications, maximising control and functionality in unique projects."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.tools.grafana"
                                        defaultMessage="<b>Grafana:</b> Provides extensive, customisable dashboards with high-resolution, per-second time-series database access to monitor hundreds of Victron system parameters."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.tools.automaticConfiguration"
                                        defaultMessage="<b>Automatic configuration & commissioning:</b> Streamline system setup and deployment, speeding up production, and reducing points of error."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                            </List>
                        </InfoAccordion>
                        <InfoAccordion
                            onChange={() => handleAccordionClick(ACCORDION_SECTIONS.SUPPORTING_INSTALLERS)}
                            expanded={accordionStates.supporting_installers}
                            title={
                                <FormattedMessage
                                    id="softwareIntegrator.supportingInstallers.title"
                                    defaultMessage="Supporting installers with tailored solutions"
                                />
                            }
                        >
                            <p>
                                <FormattedMessage
                                    id="softwareIntegrator.supportingInstallers.description"
                                    defaultMessage="If you are an installer looking to streamline your workflow, this program opens up new possibilities, including:"
                                />
                            </p>
                            <List sx={{ listStyleType: 'disc', '& > li': { marginLeft: 2 } }}>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.supportingInstallers.developUniqueFeatures"
                                        defaultMessage="<b>Develop unique features:</b> Do you have a customer case that demands new capabilities? Our program allows you to build your own solutions, tailored to your specific needs."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.supportingInstallers.getHelp"
                                        defaultMessage="<b>Get help from qualified software integrators:</b> Our network of authorised integrators are ready to support you. We can connect you with the right expert to get the job done efficiently."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.supportingInstallers.customisation"
                                        defaultMessage="<b>Customisation and integration:</b> Our open platform facilitates customisation by allowing external integrators to add new features using tools like Node-RED and Signal K."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.supportingInstallers.provenSuccess"
                                        defaultMessage="<b>Proven success:</b> Many of our customers have worked with integrators in our program to develop custom solutions for their projects. You can read about some of these projects on our blog <a>here</a>."
                                        values={{
                                            b: (chunks) => <b>{chunks}</b>,
                                            a: (chunks) => (
                                                <Typography
                                                    component={'a'}
                                                    href={SOFTWARE_INTEGRATOR_BLOG_URL}
                                                    target="_blank"
                                                    className="text-spanish-blue"
                                                >
                                                    {chunks}
                                                </Typography>
                                            ),
                                        }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.supportingInstallers.fastSolutions"
                                        defaultMessage="<b>Fast and user-friendly solutions:</b> Get started quickly with low-code programming like Node-RED and other open-source tools, making development fast and straightforward."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.supportingInstallers.multiBrandIntegration"
                                        defaultMessage="<b>Multi-brand integration:</b> We facilitate the seamless integration of products from other brands into the Victron ecosystem, ensuring compatibility and flexibility for your solutions."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                            </List>
                            <p>
                                <FormattedMessage
                                    id="softwareIntegrator.supportingInstallers.description2"
                                    defaultMessage="
                                        If your company has been developing unique features and can offer these services to
                                        other installers and distributors, we invite you to join our Victron Recommended
                                        Software Integrator Program. This is a great opportunity to share your expertise and
                                        expand your business by helping others enhance their capabilities. For now,
                                        participation is limited to one expert per topic."
                                />
                            </p>
                        </InfoAccordion>
                        <InfoAccordion
                            onChange={() => handleAccordionClick(ACCORDION_SECTIONS.SOFTWARE_INTEGRATORS)}
                            expanded={accordionStates.software_integrators}
                            title={
                                <FormattedMessage
                                    id="softwareIntegrator.forSoftwareIntegrators.title"
                                    defaultMessage="For software integrators"
                                />
                            }
                        >
                            <p>
                                <FormattedMessage
                                    id="softwareIntegrator.forSoftwareIntegrators.description"
                                    defaultMessage="Joining the program provides a range of benefits, including:"
                                />
                            </p>
                            <List sx={{ listStyleType: 'disc', '& > li': { marginLeft: 2 } }}>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.forSoftwareIntegrators.customSolutions"
                                        defaultMessage="<b>Easily create custom solutions:</b> Create tailored
                                            features effortlessly using Node-RED and Signal K Server on Venus OS."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.forSoftwareIntegrators.advancedIntegration"
                                        defaultMessage="<b>Advanced integration:</b> Easily connect to the Victron ecosystem through our
                                            VRM API from any device or platform. Additionally, integrate products from other
                                            brands to ensure flexible and comprehensive solutions."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.forSoftwareIntegrators.communitySupport"
                                        defaultMessage="<b>Expert community support:</b> Be a part of a global network of integrators to collaborate, share knowledge, and solve challenges together."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.forSoftwareIntegrators.documentation"
                                        defaultMessage="<b>Comprehensive documentation:</b> Utilise detailed guides and resources for APIs and Node-RED, supported by Victron's in-depth expertise."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                                <ListItem sx={{ display: 'list-item' }}>
                                    <FormattedMessage
                                        id="softwareIntegrator.forSoftwareIntegrators.globalNetwork"
                                        defaultMessage="<b>Global network:</b> Gain exposure as a recommended software integrator, connect with new clients worldwide, and expand your business reach through Victron's extensive global network."
                                        values={{ b: (chunks) => <b>{chunks}</b> }}
                                    />
                                </ListItem>
                            </List>
                            <p>
                                <FormattedMessage
                                    id="softwareIntegrator.forSoftwareIntegrators.description2"
                                    defaultMessage="If this sounds like a good fit for you, we'd love to hear from you. Apply <a>here</a> to join the program and start building custom solutions with Victron."
                                    values={{
                                        a: (chunks) => (
                                            <Typography
                                                component="a"
                                                href={SOFTWARE_INTEGRATOR_FORM_URL}
                                                target="_blank"
                                                className="text-spanish-blue"
                                            >
                                                {chunks}
                                            </Typography>
                                        ),
                                    }}
                                />
                            </p>
                        </InfoAccordion>
                        <InfoAccordion
                            onChange={() => handleAccordionClick(ACCORDION_SECTIONS.NEXT_STEPS)}
                            expanded={accordionStates.next_steps}
                            title={<FormattedMessage id="common.qa" defaultMessage="Q&A" />}
                        >
                            <div>
                                <p>
                                    <b>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.question1"
                                            defaultMessage="1 &ndash; What is the Victron Recommended Software Integrator Program, or to keep it short SIP?"
                                        />
                                    </b>
                                </p>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer1.part1"
                                        defaultMessage="
                                        Victron systems are highly customisable, and for most applications, our standard
                                        solutions work well. But some applications require more tailored solutions, such
                                        as an <a1>ESS system working in challenging conditions</a1>, or <a2>unusual applications</a2>. These solutions can only be created by software integrators who have a deep
                                        understanding of Victron systems and strong problem-solving skills."
                                        values={{
                                            a1: (chunks) => (
                                                <Typography
                                                    component={'a'}
                                                    href={RECOMMENDED_SOFTWARE_INTEGRATOR_BLOG_URL}
                                                    target="_blank"
                                                    className="text-spanish-blue"
                                                >
                                                    {chunks}
                                                </Typography>
                                            ),
                                            a2: (chunks) => (
                                                <Typography
                                                    component={'a'}
                                                    href={RECOMMENDED_SOFTWARE_INTEGRATOR_BLOG_URL_2}
                                                    target="_blank"
                                                    className="text-spanish-blue"
                                                >
                                                    {chunks}
                                                </Typography>
                                            ),
                                        }}
                                    />
                                </p>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer1.part2"
                                        defaultMessage="
                                        That is why we created the Software Integrator Program. To build a network of
                                        skilled software integrators, so we can provide our customers with a trusted
                                        resource to find the right specialist for their unique project."
                                    />
                                </p>
                            </div>

                            <div>
                                <p>
                                    <b>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.question2"
                                            defaultMessage="
                                            2 &ndash; What the Victron Recommended Software Integrator Program is 
                                            <strong>not</strong>"
                                            values={{
                                                strong: (chunks) => (
                                                    <strong className="text-primary-600">{chunks}</strong>
                                                ),
                                            }}
                                        />
                                    </b>
                                </p>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer2.part1"
                                        defaultMessage="
                                        We've created this program to help our customers easily find a trusted software
                                        integrator for their unique project, but we are in no way involved in this
                                        process, so we don't:"
                                    />
                                </p>
                                <List sx={{ listStyleType: 'disc', '& > li': { marginLeft: 2 } }}>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer2.part2.1"
                                            defaultMessage="keep a list of ongoing projects"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer2.part2.2"
                                            defaultMessage="get a fee for any work carried out"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer2.part2.3"
                                            defaultMessage="take part in intellectual property discussions &ndash; This is between the client and the software integrator"
                                        />
                                    </ListItem>
                                </List>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer2.part3"
                                        defaultMessage="So, if you've completed a cool project, we would love to hear about it &ndash; but we aren't involved in the process."
                                    />
                                </p>
                            </div>
                            <div>
                                <p>
                                    <b>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.question3"
                                            defaultMessage="3 &ndash; Why do we need skilled software integrators?"
                                        />
                                    </b>
                                </p>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer3.part1"
                                        defaultMessage="Software development and deployment is a skill that takes a lot of time to learn and maintain, and for many of our customers who specialise in installations, this is a hurdle to add to their portfolio."
                                    />
                                </p>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer3.part2"
                                        defaultMessage="We also cannot cater for this need directly, as we focus on the development and manufacturing of power electronics. 
                                            However, we love seeing skilled Victron enthusiasts creating their own unique, smart solutions and sharing these with us in the Victron 
                                            community. That's why we provide open-source tools to enable
                                            these cool projects. And we also want our customers to benefit from these
                                            creative minds, so we've created a network of selected freelance software
                                            integrators, supported by Victron with the right tools to fill this gap."
                                    />
                                </p>
                            </div>
                            <div>
                                <p>
                                    <b>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.question4"
                                            defaultMessage="4 &ndash; How can our customers benefit from this program?"
                                        />
                                    </b>
                                </p>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer4.part1"
                                        defaultMessage="There are broadly four reasons why our customers might need a software integrator:"
                                    />
                                </p>
                                <List sx={{ listStyleType: 'disc', '& > li': { marginLeft: 2 } }}>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer4.part1.1"
                                            defaultMessage="The system requires tailored logic to optimally perform the energy management task using, for example, Node-RED or Home Assistant"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer4.part1.2"
                                            defaultMessage="The system requires bespoke software for efficient commissioning (for example, testing the system or branding) of many similar systems (custom Venus or VRM API)"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer4.part1.3"
                                            defaultMessage="The system requires actions to be performed on a fleet level based on certain external conditions (VRM API)"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer4.part1.4"
                                            defaultMessage="The system needs a feature that Victron currently doesn't offer but could be made using external software/hardware"
                                        />
                                    </ListItem>
                                </List>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer4.part2"
                                        defaultMessage="But we would like to emphasise that we only recommend customisation when there are no standard solutions available."
                                    />
                                </p>
                            </div>
                            <div>
                                <p>
                                    <b>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.question5"
                                            defaultMessage="5 &ndash; How do we support the integrators in the program?"
                                        />
                                    </b>
                                </p>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer5.part1"
                                        defaultMessage="We provide access to Victron's API and documentation, giving them the tools they
                                            need to create unique solutions. We also promote them through our Victron blog
                                            and social media to help them reach a wider audience by introducing them to our
                                            installers and distributors."
                                    />
                                </p>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer5.part2"
                                        defaultMessage="Once enrolled in the program, integrators become part of a community where they
                                            can learn from each other and grow their skills."
                                    />
                                </p>
                            </div>
                            <div>
                                <p>
                                    <b>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.question6"
                                            defaultMessage="6 &ndash; Where can enthusiasts learn more?"
                                        />
                                    </b>
                                </p>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer6"
                                        defaultMessage="Victron has always strived to provide as much information as possible to
                                            passionate developers. There are four starting points for developer information:"
                                    />
                                </p>
                                <List sx={{ listStyleType: 'disc', '& > li': { marginLeft: 2 } }}>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer6.1"
                                            defaultMessage="Software overview Google Docs &ndash; Free to access"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer6.2"
                                            defaultMessage="Main website TechDocs &ndash; Technical information at Victron Energy"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer6.3"
                                            defaultMessage="Victron Live documentation server &ndash; e.g., Venus OS: Root Access (use the sitemap)"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer6.4"
                                            defaultMessage="Victron developer tools for Distributors &ndash; E-order API, website API, PIM (on request)"
                                        />
                                    </ListItem>
                                </List>
                            </div>
                            <div>
                                <p>
                                    <b>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.question7"
                                            defaultMessage="Victron public software repositories"
                                        />
                                    </b>
                                </p>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer7.part1"
                                        defaultMessage="As part of Victron's efforts to be as open as possible, we have 88 repositories (not all active). These underpin a lot of the customisation of our products. If you have time, join one that fits your interest:"
                                    />
                                </p>
                                <List sx={{ listStyleType: 'disc', '& > li': { marginLeft: 2 } }}>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer7.part1.1"
                                            defaultMessage="Venus OS"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer7.part1.2"
                                            defaultMessage="Grafana"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer7.part1.3"
                                            defaultMessage="Node-RED"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer7.part1.4"
                                            defaultMessage="GUI v2"
                                        />
                                    </ListItem>
                                </List>
                                <p>
                                    <FormattedMessage
                                        id="softwareIntegrator.qa.answer7.part2"
                                        defaultMessage="If one thing makes Victron special, it's our very active community. Here you can find help on various topics. We'd like to highlight:"
                                    />
                                </p>
                                <List sx={{ listStyleType: 'disc', '& > li': { marginLeft: 2 } }}>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <b>
                                            <FormattedMessage
                                                id="softwareIntegrator.qa.answer7.part2.1"
                                                defaultMessage="Software Integrators Community"
                                            />
                                        </b>
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <FormattedMessage
                                            id="softwareIntegrator.qa.answer7.part2.2"
                                            defaultMessage="<b>Beta firmware and Modifications</b> on Community"
                                            values={{ b: (chunks) => <b>{chunks}</b> }}
                                        />
                                    </ListItem>
                                    <ListItem sx={{ display: 'list-item' }}>
                                        <b>
                                            <FormattedMessage
                                                id="softwareIntegrator.qa.answer7.part2.3"
                                                defaultMessage="Victron developer resources"
                                            />
                                        </b>
                                    </ListItem>
                                </List>
                            </div>
                        </InfoAccordion>
                    </div>
                    <div className="lg:flex-[0.4] hidden lg:block">
                        <ContactBox sticky onClickContact={handleContactFormOpenFactory(true)} />
                    </div>
                </div>
            </div>
            <div className="bg-[#f4f3f1] mt-32 py-10">
                <div className="max-w-[1440px] px-4 md:px-16 mx-auto">
                    <div className="flex flex-wrap gap-y-2 justify-between items-center">
                        <Typography variant="h3" className="font-museo font-bold text-3xl lg:text-4xl">
                            <FormattedMessage id="blog.relatedPosts" defaultMessage="Related Blog Posts" />
                        </Typography>
                        <Typography component={Link} to="/app/blog/?category=264" className="text-blog-primary-600">
                            <FormattedMessage id="common.showAll" defaultMessage="Show All" />
                            <ArrowRightAlt className="ml-1" />
                        </Typography>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 mt-8">
                        {relatedBlogPosts?.data.map((post) => (
                            <BlogCard
                                author={post.author}
                                categories={[]}
                                date={new Date(post.date)}
                                slug={post.slug}
                                summary={post.excerpt}
                                title={post.title}
                                image={post.image}
                                key={post.id}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <div className="absolute bottom-0 right-0 p-4 text-right lg:hidden">
                <IconButton onClick={handleContactDrawerFactory(true)} className="text-white bg-primary-600">
                    <MessageIcon />
                </IconButton>
            </div>
            <Drawer open={contactBoxOpen} anchor="bottom" onClose={handleContactDrawerFactory(false)}>
                <ContactBox
                    onClickContact={handleContactFormOpenFactory(true)}
                    onCloseContact={handleContactFormOpenFactory(false)}
                    onClose={handleContactDrawerFactory(false)}
                />
            </Drawer>
            <MapDialog
                open={Boolean(openedIntegrator) && !contactFormOpen}
                onClose={() => handleChooseIntegrator(null)}
                integrator={openedIntegrator}
                onMarkerButtonClick={handleChooseIntegrator}
                onContactButtonClick={handleContactFormOpenFactory(true)}
            />
            <ContactForm
                open={contactFormOpen}
                onClose={handleContactFormOpenFactory(false)}
                integrator={openedIntegrator}
            />
        </>
    );
};
